// The commercial follow-up queue, computed from the records already loaded
// (stakeholders, outreach, accounts and campaigns). One source for
// FollowupCenter and for the priorities suggested in Mi espacio › Hoy: neither
// copies follow-ups anywhere, both read the same records.
//
// Only interactions that count are used (src/outreachEvidence.js): an activity
// logged because Gmail, WhatsApp or LinkedIn was opened never takes anyone out
// of "Te respondieron" and never counts as "contacted" (days since the last
// contact, contacted today). Those records stay in the history as they are.
import { outreachKind, countsAsInteraction, countsAsContact, focusFrom } from './outreachEvidence.js';

const field = (r, k) => { const v = r?.fields?.[k]; return v && typeof v === 'object' && !Array.isArray(v) ? (v.name ?? v.value ?? '') : (v ?? ''); };
const ids = (r, k) => { const v = r?.fields?.[k]; return Array.isArray(v) ? v.map(x => (typeof x === 'object' ? x.id || x : x)) : []; };
const DAY = 86400000;
const byDateDesc = (a, b) => new Date(b.fields?.['Date'] || 0) - new Date(a.fields?.['Date'] || 0);

/** Each contact's activity, newest first (one pass over the outreach). */
export function outreachByContact(outreach = []) {
  const map = new Map();
  for (const o of outreach) for (const id of ids(o, 'Stakeholder')) { if (!map.has(id)) map.set(id, []); map.get(id).push(o); }
  for (const list of map.values()) list.sort(byDateDesc);
  return map;
}

/**
 * The facts of the queue for one contact, from the interactions that count:
 * days since the last real contact, contacted today, replied before, and the
 * "channel opened" records after the last interaction (shown as a hint only).
 */
export function contactFacts(list = [], now = Date.now()) {
  const counted = list.filter(countsAsInteraction);
  const last = counted[0] || null;
  const lastDate = last ? new Date(last.fields?.['Date'] || 0) : null;
  const daysSince = lastDate ? Math.floor((now - lastDate) / DAY) : null;
  const contactedToday = lastDate ? lastDate.toDateString() === new Date(now).toDateString() : false;
  const lastTime = lastDate ? lastDate.getTime() : -Infinity;
  const openedAfter = list.filter(o => outreachKind(o) === 'opened' && new Date(o.fields?.['Date'] || 0).getTime() >= lastTime).length;
  return {
    last, lastDate, daysSince, contactedToday, openedAfter,
    totalTouches: counted.length, hasReplied: counted.some(o => outreachKind(o) === 'inbound'),
    lastContact: list.find(countsAsContact) || null,
  };
}

/**
 * Contacts whose latest interaction that counts is theirs (Received or Replied): they are
 * waiting for an answer. `keep(s)` filters (search, influence…).
 */
export function pendingReplies({ stakeholders = [], outreach = [], keep = () => true, now = Date.now(), byContact = null }) {
  const map = byContact || outreachByContact(outreach);
  const results = [];
  for (const s of stakeholders) {
    const facts = contactFacts(map.get(s.id) || [], now);
    if (!facts.last) continue;
    const status = String(field(facts.last, 'Status') || '').toLowerCase();
    if (status !== 'received' && status !== 'replied') continue;
    if (!keep(s)) continue;
    results.push({ s, lastOutreach: facts.last, daysSince: facts.daysSince, openedAfter: facts.openedAfter });
  }
  return results.sort((a, b) => a.daysSince - b.daysSince);
}

/**
 * Contact states that leave the automatic follow-ups by age (decided by Ale, 03-11-2026): Not Interested, DNC,
 * Bounced and Left Company are out; Nurture is out of the reminders by age but may have a next action with a date
 * (a Personal Task, Hoy's follow-up panel). Answers they send still appear in «Te respondieron».
 */
export const AUTO_FOLLOWUP_EXCLUDED = ['Not Interested', 'DNC', 'Bounced', 'Left Company', 'Nurture'];

/**
 * Contacts to follow up (urgent, follow-up, re-engage), by the same focus formula as the
 * commercial enrichment plus active campaigns, but computed only from interactions that
 * count. Never contacted today or in the last 3 days, and only contacts touched at least once.
 */
export function focusItems({ stakeholders = [], outreach = [], accounts = [], campaigns = [], keep = () => true, now = Date.now(), byContact = null }) {
  const map = byContact || outreachByContact(outreach);
  const results = [];
  for (const s of stakeholders) {
    if (AUTO_FOLLOWUP_EXCLUDED.includes(String(field(s, 'Status') || '').trim())) continue;
    const facts = contactFacts(map.get(s.id) || [], now);
    if (facts.contactedToday) continue;
    if (facts.daysSince !== null && facts.daysSince < 3) continue;
    if (!keep(s)) continue;
    if (facts.totalTouches === 0) continue;
    const { focusScore, focusTag } = focusFrom({ daysSince: facts.daysSince, influence: field(s, 'Level of Influence'), hasReplied: facts.hasReplied, contactedToday: facts.contactedToday });
    if (!focusTag) continue;
    let score = focusScore;
    if (campaigns.some(c => ids(c, 'Assigned Stakeholders').includes(s.id) && field(c, 'Status') !== 'Inactive')) score += 12;
    const accId = ids(s, 'Account')[0];
    const acc = accId ? accounts.find(a => a.id === accId) : null;
    if (acc?._enriched?.inActiveCampaign) score += 8;
    // `e`: the enrichment the screens already show, with the queue's own facts on top
    const e = { ...(s._enriched || {}), daysSince: facts.daysSince, lastDate: facts.lastDate, contactedToday: facts.contactedToday, totalTouches: facts.totalTouches, hasReplied: facts.hasReplied, focusScore, focusTag, openedAfter: facts.openedAfter };
    results.push({ s, acc, score, tag: focusTag, e });
  }
  return results.sort((a, b) => b.score - a.score);
}

/** The follow-ups dismissed today in this browser (FollowupCenter's "Descartar hoy"). */
export function dismissedToday(storageKey) {
  try {
    const raw = JSON.parse(localStorage.getItem(storageKey) || '{}');
    const today = new Date().toDateString();
    return new Set(Object.entries(raw).filter(([, d]) => d === today).map(([id]) => id));
  } catch { return new Set(); }
}
export const DISMISS_FOLLOWUPS_KEY = 'oike_dismissed_followups';
export const DISMISS_REPLIES_KEY = 'oike_dismissed_replies';
